import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useResetPasswordMutation } from '~entities/parent/api/reset-password';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';

import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~shared/shadcn/ui/form';
import { Input } from '~shared/shadcn/ui/input';

interface ResetPasswordProps {
  email: string;
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const ResetPassword = ({ email, open, setOpen }: ResetPasswordProps) => {
  const mutate = useResetPasswordMutation();
  const { data: studioDetails } = useGetStudioDetails();
  const validatePassword = (password: string) => {
    const requirements = {
      uppercase: /[A-Z]/.test(password),
      digit: /\d/.test(password),
      symbol: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      minLength: password.length >= 8,
    };
    return requirements;
  };

  const resetPasswordSchema = z
    .object({
      password: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .refine(
          (password) => /[A-Z]/.test(password),
          'Password must contain at least one uppercase letter',
        )
        .refine(
          (password) => /\d/.test(password),
          'Password must contain at least one number',
        )
        .refine(
          (password) => /[!@#$%^&*(),.?":{}|<>]/.test(password),
          'Password must contain at least one special character',
        ),
      confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: "Passwords don't match",
      path: ['confirmPassword'],
    });

  type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

  const resetPasswordForm = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  });

  const watchPassword = resetPasswordForm.watch('password');
  const [requirements, setRequirements] = useState(
    validatePassword(watchPassword),
  );

  useEffect(() => {
    setRequirements(validatePassword(watchPassword));
  }, [watchPassword]);

  const onResetPasswordSubmit = async (values: ResetPasswordFormValues) => {
    if (!studioDetails) return;
    try {
      await mutate.mutateAsync({
        email,
        locationId: studioDetails.locationId,
        password: values.password,
      });
      toast.success('Password reset successfully');
      setOpen(false);
      resetPasswordForm.reset();
    } catch (error) {
      console.error(error);
      toast.error('Failed to reset password');
    }
    console.log(values);
    // Handle password reset logic here
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset Password</DialogTitle>
          <DialogDescription>
            Please choose a new password for your account: {email}
          </DialogDescription>
        </DialogHeader>
        <Form {...resetPasswordForm}>
          <form
            onSubmit={resetPasswordForm.handleSubmit(onResetPasswordSubmit)}
            className='space-y-4'
          >
            <FormField
              control={resetPasswordForm.control}
              name='password'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input
                      type='password'
                      placeholder='Enter new password'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className='mt-2 space-y-1 text-sm'>
              <p
                className={
                  requirements.minLength ? 'text-green-600' : 'text-gray-500'
                }
              >
                • Minimum 8 characters
              </p>
              <p
                className={
                  requirements.uppercase ? 'text-green-600' : 'text-gray-500'
                }
              >
                • At least one uppercase letter
              </p>
              <p
                className={
                  requirements.digit ? 'text-green-600' : 'text-gray-500'
                }
              >
                • At least one number
              </p>
              <p
                className={
                  requirements.symbol ? 'text-green-600' : 'text-gray-500'
                }
              >
                {`• At least one special character (!@#$%^&*(),.?":{|})<>`}
              </p>
            </div>
            <FormField
              control={resetPasswordForm.control}
              name='confirmPassword'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm Password</FormLabel>
                  <FormControl>
                    <Input
                      type='password'
                      placeholder='Confirm new password'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type='button'
                variant='secondary'
                onClick={() => {
                  resetPasswordForm.reset();
                  setOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button type='submit' disabled={mutate.isPending}>
                {mutate.isPending && <Loader2 className='mr-2 animate-spin' />}
                Reset Password
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
